import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { needsAddressForSlot } from '@website/app/[locale]/activity/[id]/slots/lib';

export type StepName = 'select-time' | 'select-address' | 'payment' | 'status';

export interface StepConfig {
  key: StepName;
  titleKey: string; // For headers (actionable)
  stepperTitleKey: string; // For stepper (simple labels)
  step: number;
  showFor: ('at-home' | 'at-venue')[];
}

export const useBookingSteps = (
  isAtHome: boolean,
  chosenSlotKind?: 'venue' | 'at_home' | 'online',
  fallbackLocationKind?: 'venue' | 'at_home' | 'online'
) => {
  const t = useTranslations();

  // Address is needed only for an in-person AT-HOME session, gated on the CHOSEN
  // slot's own resolved kind (a mixed activity may offer venue + at_home slots).
  // Falls back to the activity-level at-home hint (refined by the booking's
  // location kind) only when no slot kind was captured yet (legacy slots with no
  // referenced location). Shared helper so the stepper, the continue router, and
  // the validators can never diverge.
  const needsAddress = needsAddressForSlot(chosenSlotKind, isAtHome, fallbackLocationKind);

  // Define all possible steps with metadata
  const ALL_STEPS: StepConfig[] = useMemo(
    () => [
      {
        key: 'select-time',
        titleKey: 'Booking.steps.pickSlot',
        stepperTitleKey: 'Booking.steps.slots',
        step: 1,
        showFor: ['at-home', 'at-venue']
      },
      {
        key: 'select-address',
        titleKey: 'Booking.steps.selectAddress',
        stepperTitleKey: 'Booking.steps.address',
        step: 2,
        showFor: ['at-home']
      },
      {
        key: 'payment',
        titleKey: 'Booking.steps.payment',
        stepperTitleKey: 'Booking.steps.payment',
        step: needsAddress ? 3 : 2,
        showFor: ['at-home', 'at-venue']
      },
      {
        key: 'status',
        titleKey: 'Booking.steps.status',
        stepperTitleKey: 'Booking.steps.status',
        step: needsAddress ? 4 : 3,
        showFor: ['at-home', 'at-venue']
      }
    ],
    [needsAddress]
  );

  // Generate step flow: the address step is included only when an address is
  // actually needed (in-person at-home), so online bookings skip it.
  const stepFlow = useMemo(() => {
    const activityType = needsAddress ? 'at-home' : 'at-venue';
    return ALL_STEPS.filter((step) => step.showFor.includes(activityType));
  }, [ALL_STEPS, needsAddress]);

  // Helper functions
  const getStepByKey = (stepKey: StepName) => {
    return stepFlow.find((step) => step.key === stepKey);
  };

  const getStepTitle = (stepKey: StepName) => {
    const step = getStepByKey(stepKey);
    return step ? t(step.titleKey as any) : '';
  };

  const getStepperTitle = (stepKey: StepName) => {
    const step = getStepByKey(stepKey);
    return step ? t(step.stepperTitleKey as any) : '';
  };

  const getNextStep = (currentStep: StepName): StepName | null => {
    const currentIndex = stepFlow.findIndex((step) => step.key === currentStep);
    const nextIndex = currentIndex + 1;
    return nextIndex < stepFlow.length ? stepFlow[nextIndex].key : null;
  };

  const getPreviousStep = (currentStep: StepName): StepName | null => {
    const currentIndex = stepFlow.findIndex((step) => step.key === currentStep);
    const prevIndex = currentIndex - 1;
    return prevIndex >= 0 ? stepFlow[prevIndex].key : null;
  };

  const isFirstStep = (stepKey: StepName) => {
    return stepFlow[0]?.key === stepKey;
  };

  const isLastStep = (stepKey: StepName) => {
    return stepFlow[stepFlow.length - 1]?.key === stepKey;
  };

  const getStepNumber = (stepKey: StepName) => {
    const stepIndex = stepFlow.findIndex((step) => step.key === stepKey);
    return stepIndex + 1; // 1-based numbering
  };

  const getTotalSteps = () => {
    return stepFlow.length;
  };

  return {
    stepFlow,
    getStepByKey,
    getStepTitle,
    getStepperTitle,
    getNextStep,
    getPreviousStep,
    isFirstStep,
    isLastStep,
    getStepNumber,
    getTotalSteps
  };
};
